import { useState } from 'react';
import { Modal } from './Modal.tsx';
import { MediaThumb } from './MediaThumb.tsx';
import { Loading, EmptyState } from './ui.tsx';
import { useMedia } from '../api/media.ts';
import { MediaUploadIcon } from '../onboarding/icons.tsx';

/**
 * Pick a single image from the media library. Reuses the playlist picker's
 * `.picker-item` styling. Image-only by default (logos, brand marks).
 */
export function MediaPickerModal({
  title = 'Choose an image',
  onPick,
  onClose,
}: {
  title?: string;
  onPick: (mediaId: string) => void;
  onClose: () => void;
}) {
  const media = useMedia({ kind: 'image' });
  const [search, setSearch] = useState('');

  const items = (media.data ?? []).filter((m) =>
    search ? (m.filename ?? '').toLowerCase().includes(search.toLowerCase()) : true,
  );

  return (
    <Modal title={title} onClose={onClose}>
      <input
        className="mb-4"
        aria-label="Search images"
        placeholder="Search images…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {media.isLoading ? (
        <Loading />
      ) : items.length === 0 ? (
        <EmptyState icon={<MediaUploadIcon />} title="No images yet">
          <p className="text-dim">Upload images on the Media page, then pick one here.</p>
        </EmptyState>
      ) : (
        <div style={{ maxHeight: 420, overflow: 'auto' }}>
          {items.map((m) => (
            <div
              key={m.id}
              className="picker-item"
              role="button"
              tabIndex={0}
              onClick={() => onPick(m.id)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onPick(m.id)}
            >
              <MediaThumb media={m} className="picker-thumb" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  className="cell-strong"
                  style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {m.filename}
                </div>
                <div className="cell-dim" style={{ fontSize: 'var(--fs-xs)' }}>
                  {m.width && m.height ? `${m.width}×${m.height}` : 'image'}
                </div>
              </div>
              <span className="text-dim">Select</span>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
